import {
  Component,
  ElementRef,
  inject,
  input,
  OnInit,
  signal,
  ViewChild,
  AfterViewInit
} from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { filter, finalize, tap } from 'rxjs';
import { SharedModule } from '~app/app/~shared/modules/shared.module';
import { ResourceFacade } from '~app/app/~store/facades/resource.facade';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { BaseComponent } from '~app/app/~shared/abstract-components/base.component';
import { MatDialogRef } from '@angular/material/dialog';
import { AdvertiserCreationDialogComponent } from '../../modals/advertiser-creation-dialog/advertiser-creation-dialog.component';
import { Advertiser, AdvertiserData } from '~core/models/advertiser.models';
import { AdvertiserService } from '~app/app/~shared/services/advertiser.service';
import slugify from '@sindresorhus/slugify';
import { ConstantsService } from '~core/services/constants.service';
import intlTelInput from 'intl-tel-input';
import { phoneNumberValidator } from '~app/app/~shared/validators/phone-number.validator';
import { CountryCode } from 'libphonenumber-js';
import { removeNilProperties } from '~app/app/~shared/utils/utils';

@Component({
  selector: 'advertiser-creation-form',
  templateUrl: './advertiser-creation-form.component.html',
  styleUrl: './advertiser-creation-form.component.scss',
  standalone: true,
  imports: [SharedModule]
})
export class AdvertiserCreationFormComponent
  extends BaseComponent
  implements OnInit, AfterViewInit
{
  private _advertiserService = inject(AdvertiserService);
  private _resourceFacade = inject(ResourceFacade);
  private _dialogRef = inject(MatDialogRef<AdvertiserCreationDialogComponent>);
  private _constantService = inject(ConstantsService);

  @ViewChild('phoneNumberInput') intlInputPhone?: ElementRef;

  advertiser = input<Advertiser>();

  violations$ = this._globalFacade.formViolations$.pipe(
    tap({
      next: violations => {
        setTimeout(() => {
          violations?.forEach(violation =>
            this.form.get(violation.propertyPath)?.setErrors({
              httpError: violation.title
            })
          );
        }, 500);
      }
    })
  );

  phoneNumberIntlPluginRef?: intlTelInput.Plugin;

  isSubmitting = signal(false);
  countries$ = this._constantService.getCountryListWithDialCode();

  defaultCountry = signal('ci');
  form = new FormGroup({
    name: new FormControl<string>('', {
      validators: [Validators.required, Validators.min(3)]
    }),
    email: new FormControl<string>('', {
      validators: []
    }),
    phoneNumber: new FormControl<string>('', {
      validators: [
        phoneNumberValidator(
          this.phoneNumberIntlPluginRef?.getSelectedCountryData()
            .iso2 as CountryCode
        )
      ]
    }),
    description: new FormControl<string>('', {
      validators: []
    }),
    country: new FormControl<string>('CI', {
      validators: [Validators.required]
    }),
    address: new FormControl<string>('', {
      validators: []
    })
  });

  ngOnInit(): void {
    if (this.advertiser()) {
      this.form.patchValue({
        name: this.advertiser()?.name,
        description: this.advertiser()?.description,
        country: this.advertiser()?.country,
        address: this.advertiser()?.address,
        email: this.advertiser()?.email,
        phoneNumber: this.advertiser()?.phoneNumber
      });
    }
    this.form
      .get('country')
      ?.valueChanges.pipe(
        takeUntilDestroyed(this._destroyRef),
        filter(Boolean),
        tap(country => {
          this.defaultCountry.set(country.toLowerCase());
          this.phoneNumberIntlPluginRef?.setCountry(country.toLowerCase());
        })
      )
      .subscribe();
  }

  ngAfterViewInit(): void {
    this.initPhoneNumberInputs();
  }

  onSubmit(): void {
    this.isSubmitting.set(true);
    const advertiserId = this.advertiser()?.id;
    const formValue = this.form.value;
    const values: AdvertiserData = {
      name: formValue.name as string,
      country: formValue.country as string,
      code: formValue.name ? slugify(formValue.name) : '',
      address: formValue.address as string,
      phoneNumber: formValue.phoneNumber as string,
      email: formValue.email as string,
      description: formValue.description as string
    };

    if (advertiserId) {
      this.updateAdvertiser(advertiserId, values);
    } else {
      this.createAdvertiser(values);
    }
  }

  createAdvertiser(values: AdvertiserData): void {
    const data = removeNilProperties(values);
    this._advertiserService
      .create(data as AdvertiserData)
      .pipe(
        takeUntilDestroyed(this._destroyRef),
        tap({
          next: () => {
            this._dialogRef.close(true);
            this._snackbarService.open({
              text: `Annonceur créé avec succès`,
              type: 'success'
            });

            this._resourceFacade.getData({ refresh: true });
          }
        }),
        finalize(() => this.isSubmitting.set(false))
      )
      .subscribe();
  }

  updateAdvertiser(groupId: string, values: AdvertiserData): void {
    this._advertiserService
      .update(groupId, values)
      .pipe(
        takeUntilDestroyed(this._destroyRef),
        tap({
          next: () => {
            this._dialogRef.close(true);
            this._snackbarService.open({
              text: `Annonceur modifié avec succès`,
              type: 'success'
            });

            this._resourceFacade.getData({ refresh: true });
          }
        }),
        finalize(() => this.isSubmitting.set(false))
      )
      .subscribe();
  }

  initPhoneNumberInputs(): void {
    if (this.intlInputPhone?.nativeElement) {
      this.phoneNumberIntlPluginRef = intlTelInput(
        this.intlInputPhone.nativeElement,
        {
          allowDropdown: true,
          formatOnDisplay: true,
          autoInsertDialCode: true,
          nationalMode: false,
          initialCountry: this.defaultCountry(),
          separateDialCode: true,
          customContainer: 'country',
          dropdownContainer: document.body
        }
      );
    }
  }
}
